import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Container,
  Toolbar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import ClassTypeForm from '../components/ClassTypeForm';
import { DataTable, StatusBadge } from '../components/ui';

const ClassTypeListPage = () => {
  const { t } = useTranslation();
  const [classTypes, setClassTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Dialog states
  const [formDialog, setFormDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedClassType, setSelectedClassType] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Categories and difficulties
  const [categories, setCategories] = useState([]);
  const [difficulties, setDifficulties] = useState([]);

  const fetchClassTypes = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (searchTerm) params.append('search', searchTerm);
      if (categoryFilter) params.append('category', categoryFilter);
      if (difficultyFilter) params.append('difficulty', difficultyFilter);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await api.get(`/class-types/all?${params.toString()}`);
      setClassTypes(response.data.data);
      setTotalCount(response.data.count);
      setError('');
    } catch (err) {
      setError(t('admin:classTypes.errors.loadFailed'));
      console.error('Error fetching class types:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, categoryFilter, difficultyFilter, statusFilter, t]);

  const fetchMetadata = useCallback(async () => {
    try {
      const [categoriesRes, difficultiesRes] = await Promise.all([
        api.get('/class-types/meta/categories'),
        api.get('/class-types/meta/difficulties')
      ]);

      setCategories(categoriesRes.data.data);
      setDifficulties(difficultiesRes.data.data);
    } catch (err) {
      console.error('Error fetching metadata:', err);
    }
  }, []);

  useEffect(() => {
    fetchClassTypes();
    fetchMetadata();
  }, [page, pageSize, fetchClassTypes, fetchMetadata]);

  const handleCreate = () => {
    setSelectedClassType(null);
    setIsEditing(false);
    setFormDialog(true);
  };

  const handleEdit = (classType) => {
    setSelectedClassType(classType);
    setIsEditing(true);
    setFormDialog(true);
  };

  const handleView = (classType) => {
    setSelectedClassType(classType);
    setViewDialog(true);
  };

  const handleDelete = (classType) => {
    setSelectedClassType(classType);
    setDeleteDialog(true);
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (isEditing) {
        await api.put(`/class-types/${selectedClassType._id}`, formData);
        setSnackbar({
          open: true,
          message: t('admin:classTypes.messages.updateSuccess'),
          severity: 'success'
        });
      } else {
        await api.post('/class-types', formData);
        setSnackbar({
          open: true,
          message: t('admin:classTypes.messages.createSuccess'),
          severity: 'success'
        });
      }
      
      setFormDialog(false);
      fetchClassTypes();
    } catch (err) {
      const errorMessage = err.response?.data?.error || t('admin:classTypes.errors.saveFailed');
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/class-types/${selectedClassType._id}`);
      setSnackbar({
        open: true,
        message: t('admin:classTypes.messages.deleteSuccess'),
        severity: 'success'
      });
      setDeleteDialog(false);
      fetchClassTypes();
    } catch (err) {
      const errorMessage = err.response?.data?.error || t('admin:classTypes.errors.deleteFailed');
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'error';
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return 'success';
      case 'intermediate':
        return 'warning';
      case 'advanced':
        return 'error';
      default:
        return 'info';
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'cardio': 'error',
      'strength': 'warning',
      'flexibility': 'success',
      'dance': 'secondary',
      'martial-arts': 'error',
      'mind-body': 'info',
      'aqua': 'primary',
      'cycling': 'warning',
      'other': 'default'
    };
    return colors[category] || 'default';
  };

  const formatDuration = (duration) => {
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    
    if (hours === 0) {
      return `${minutes}m`;
    } else if (minutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  };

  // Define table columns
  const columns = [
    {
      key: 'name',
      title: t('common:labels.name'),
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div style={{ fontWeight: 'medium' }}>
          {row.name}
        </div>
      )
    },
    {
      key: 'category',
      title: t('admin:classTypes.category'),
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <StatusBadge 
          status={getCategoryColor(row.category)}
          size="sm"
        >
          {row.category}
        </StatusBadge>
      )
    },
    {
      key: 'difficulty',
      title: t('admin:classTypes.difficulty'),
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <StatusBadge 
          status={getDifficultyColor(row.difficulty)}
          size="sm"
        >
          {row.difficulty}
        </StatusBadge>
      )
    },
    {
      key: 'duration',
      title: t('common:labels.duration'),
      sortable: true,
      render: (value, row) => formatDuration(row.duration)
    },
    {
      key: 'maxCapacity',
      title: t('common:labels.maxCapacity'),
      sortable: true,
      render: (value, row) => `${row.maxCapacity} people`
    },
    {
      key: 'isActive',
      title: t('common:labels.status'),
      sortable: true,
      render: (value, row) => (
        <StatusBadge status={getStatusColor(row.isActive)} size="sm">
          {row.isActive ? t('common:states.active') : t('common:states.inactive')}
        </StatusBadge>
      )
    }
  ];

  // Define table actions
  const actions = {
    row: [
      {
        icon: <ViewIcon />,
        title: t('common:actions.viewDetails'),
        onClick: handleView,
        variant: 'ghost'
      },
      {
        icon: <EditIcon />,
        title: t('common:buttons.edit'),
        onClick: handleEdit,
        variant: 'ghost'
      },
      {
        icon: <DeleteIcon />,
        title: t('common:buttons.delete'),
        onClick: handleDelete,
        variant: 'danger'
      }
    ]
  };

  // Define pagination config
  const paginationConfig = {
    page: page,
    pageSize: pageSize,
    total: totalCount,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [5, 10, 25, 50]
  };

  // Define empty state
  const emptyState = (
    <div>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏃‍♀️</div>
      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
        {t('admin:classTypes.emptyState.title')}
      </div>
      <div style={{ color: '#666' }}>
        {t('admin:classTypes.emptyState.description')}
      </div>
    </div>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('admin:classTypes.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('admin:classTypes.description')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Toolbar */}
      <Paper sx={{ mb: 3 }}>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap', py: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            {t('admin:classTypes.addButton')}
          </Button>
          
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Filters */}
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder={t('admin:classTypes.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ minWidth: 120, maxWidth:240 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label={t('admin:classTypes.category')}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
                label={t('admin:classTypes.difficulty')}
              >
                <MenuItem value="">All Levels</MenuItem>
                {difficulties.map((difficulty) => (
                  <MenuItem key={difficulty.value} value={difficulty.value}>
                    {difficulty.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label={t('common:labels.status')}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Toolbar>
      </Paper>

      {/* Data Table */}
      <DataTable
        data={classTypes}
        columns={columns}
        loading={loading}
        error={error}
        pagination={paginationConfig}
        sorting={{ enabled: true }}
        filtering={{ enabled: true }}
        actions={actions}
        emptyState={emptyState}
      />

      {/* Form Dialog */}
      <Dialog
        open={formDialog}
        onClose={() => setFormDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isEditing ? t('admin:classTypes.editTitle') : t('admin:classTypes.createTitle')}
        </DialogTitle>
        <DialogContent>
          <ClassTypeForm
            classType={selectedClassType}
            categories={categories}
            difficulties={difficulties}
            onSubmit={handleFormSubmit}
            onCancel={() => setFormDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('admin:classTypes.detailsTitle')}</DialogTitle>
        <DialogContent>
          {selectedClassType && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedClassType.name}
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('common:labels.description')}
                </Typography>
                <Typography variant="body1">
                  {selectedClassType.description}
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('admin:classTypes.category')}
                  </Typography>
                  <StatusBadge
                    status={getCategoryColor(selectedClassType.category)}
                    size="sm"
                  >
                    {selectedClassType.category}
                  </StatusBadge>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('admin:classTypes.difficulty')}
                  </Typography>
                  <StatusBadge
                    status={getDifficultyColor(selectedClassType.difficulty)}
                    size="sm"
                  >
                    {selectedClassType.difficulty}
                  </StatusBadge>
                </Box>
              </Stack>
              
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('common:labels.duration')}
                  </Typography>
                  <Typography variant="body1">
                    {formatDuration(selectedClassType.duration)}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('common:labels.maxCapacity')}
                  </Typography>
                  <Typography variant="body1">
                    {t('admin:classTypes.capacityPeople', { count: selectedClassType.maxCapacity })}
                  </Typography>
                </Box>
              </Stack>
              
              {selectedClassType.requiredEquipment && selectedClassType.requiredEquipment.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('admin:classTypes.requiredEquipment')}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {selectedClassType.requiredEquipment.map((equipment, index) => (
                      <StatusBadge
                        key={index}
                        variant="outline"
                        size="sm"
                      >
                        {equipment}
                      </StatusBadge>
                    ))}
                  </Stack>
                </Box>
              )}
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('common:labels.status')}
                </Typography>
                <StatusBadge
                  status={getStatusColor(selectedClassType.isActive)}
                  size="sm"
                >
                  {selectedClassType.isActive ? 'Active' : 'Inactive'}
                </StatusBadge>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>{t('common:buttons.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        maxWidth="sm"
      >
        <DialogTitle>{t('common:buttons.delete')} - {t('common:buttons.confirm')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('admin:classTypes.deleteConfirmation')} {t('common:messages.cannotUndo')}
          </Alert>
          {selectedClassType && (
            <Typography variant="body1">
              <strong>{selectedClassType.name}</strong>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>{t('common:buttons.cancel')}</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            {t('common:buttons.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ClassTypeListPage;